import clsx from "clsx";
import React, { useEffect, useRef } from "react";
import Button from "web/react/components/button/button";
import { getSelectedOptions } from "web/react/components/feeds-filters/hooks/use-filter-manager";
import LoadingSpinner from "web/react/components/loading-spinner/loading-spinner";
import { gettext } from "web/script/modules/django-i18n";
import environment from "web/script/modules/environment";
import { AllSelectedFilterInfo, StandardFilterProps } from "web/types/feed-filters";
import { UniversalFiltersData } from "web/types/universal-filters";
import BreadcrumbPills from "./feeds-filter-breadcrumb-pills/feeds-filter-breadcrumb-pills";
import FilterPagination from "./feeds-filters-pagination/filter-pagination";
import {
    PaginatedItem,
    PaginationExpansionState,
    PaginationProps,
} from "./feeds-filters-pagination/pagination-state";
import styles from "./feeds-filters.module.css";
import { useFilterScrollPosition } from "./hooks/use-filter-scroll-position";

interface FeedFiltersProps extends PaginationProps, StandardFilterProps {
    applyState(): void;
    canApply: boolean;
    displayedFilters: UniversalFiltersData;
    isLoading: boolean;
    isOpen: boolean;
    lazyFacetFetch(optionGroupId: string): void;
    lazyFacetIsLoading: boolean;
    onClose: (item: PaginatedItem) => void;
    selectedFilterInfo: AllSelectedFilterInfo;
    clearAllFilters(): void;
    resetPagination: () => void;
}

function FeedFilters({
    applyState,
    canApply,
    displayedFilters,
    isLoading,
    isOpen,
    lazyFacetFetch,
    lazyFacetIsLoading,
    onClick,
    onClose,
    onPageChange,
    paginationState,
    resetPagination,
    pendingFilterInfo,
    selectedFilterInfo,
    clearAllFilters,
}: FeedFiltersProps): JSX.Element {
    let scrollAreaRef = useRef<HTMLDivElement>(null);
    let pillContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!scrollAreaRef.current) {
            return;
        }

        if (scrollAreaRef.current.scrollTop != 0 && isOpen) {
            scrollAreaRef.current.scrollTop = 0;
        }
    }, [isOpen]);

    const { containerRef, onItemExpanded } = useFilterScrollPosition(
        scrollAreaRef,
        paginationState,
        isOpen
    );

    function handlePageChange(item: PaginatedItem, order?: number): void {
        let itemExpansionState = paginationState.getExpansionState(item);

        if (itemExpansionState != PaginationExpansionState.Collapsed) {
            if (
                environment.getFeature("seo_enable_relevant_feed_filters") &&
                item.type === "category"
            ) {
                onClick(item);
            }
            onClose(item);
            return;
        } else if (item.type == "category" && !pendingFilterInfo.getChecked(item)) {
            onClick(item);
        }

        if (isOpen && typeof order != "undefined") {
            onItemExpanded(order);
        }

        onPageChange(item);
    }

    function onBreadcrumbPillClick(item, immediateUnselect): void {
        onClick(item, immediateUnselect);

        // remove the item from the pagination state
        let itemExpansionState = paginationState.getExpansionState(item);
        if (itemExpansionState != PaginationExpansionState.Collapsed) {
            onClose(item);
        }
    }

    function onClearAllFiltersClick(): void {
        clearAllFilters();

        // also reset the pagination so filters are collapsed correctly
        resetPagination();
    }

    let selectedOptions = getSelectedOptions(selectedFilterInfo);

    const breadcrumbs = selectedOptions.map((option) => ({
        id: `${option.name}=${option.value}`,
        to: option.url,
        label: option.label,
        onClick: () => onBreadcrumbPillClick(option, false),
        hideIcon: option.name === "gender" && option.value === "all",
    }));

    return (
        <div
            className={clsx(styles.container, {
                [styles.loading]: isLoading,
            })}
        >
            <div className={styles.content} ref={scrollAreaRef}>
                <div className={styles.sticky}>
                    <div className={styles.filterHeader}>
                        <div className={styles.title}>{gettext("general.filters")}</div>
                        <button className={styles.clearAll} onClick={onClearAllFiltersClick}>
                            {gettext("feed.filters.mobile_clear_all_btn_text")}
                        </button>
                    </div>
                    <div ref={pillContainerRef}>
                        <BreadcrumbPills className={styles.breadcrumbs} breadcrumbs={breadcrumbs} />
                    </div>
                </div>
                <FilterPagination
                    ref={containerRef}
                    allSelectedFilterInfo={selectedFilterInfo}
                    sections={displayedFilters.filter_section_list}
                    onPageChange={handlePageChange}
                    lazyFacetFetch={lazyFacetFetch}
                    lazyFacetIsLoading={lazyFacetIsLoading}
                    onClick={onClick}
                    paginationState={paginationState}
                    pendingFilterInfo={pendingFilterInfo}
                />
            </div>
            <div className={styles.applyButton}>
                <Button
                    openInNewTab={false}
                    fullHeight={false}
                    styleType={"cta"}
                    onClick={applyState}
                    fullWidth={true}
                    disabled={!canApply}
                >
                    {gettext("feed.filters.apply_btn_text") /* Apply Filters */}
                    {/* // TODO: Just realised we don't have a differences count */}
                </Button>
            </div>
            {isLoading && <LoadingSpinner />}
        </div>
    );
}

export default FeedFilters;
